import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const cardVariants = cva(
  'bg-white transition-all',
  {
    variants: {
      variant: {
        // Default card with subtle border
        default: 'border border-slate-200 rounded-xl shadow-sm',

        // Elevated with more shadow
        elevated: 'border border-slate-200 rounded-xl shadow-lg',

        // Flat with no shadow
        flat: 'bg-slate-50 border border-slate-200 rounded-xl',

        // Interactive card (hoverable)
        interactive:
          'border-2 border-slate-200 rounded-xl hover:shadow-lg hover:border-slate-300 cursor-pointer',

        // Selected state
        selected: 'border-2 shadow-md rounded-xl',

        // Form card (login/signup modals)
        form: 'rounded-2xl shadow-lg border border-slate-200',

        // Ghost (minimal styling)
        ghost: 'rounded-xl',
      },
      padding: {
        none: 'p-0',
        xs: 'p-2',
        sm: 'p-3',
        md: 'p-4 sm:p-5',
        lg: 'p-6',
        xl: 'p-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      padding: 'md',
    },
  }
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  /** Dynamic border color class (e.g., "border-blue-500") */
  borderColor?: string;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, borderColor, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(cardVariants({ variant, padding, className }), borderColor)}
        {...props}
      />
    );
  }
);
Card.displayName = 'Card';

// Card subcomponents
const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-start justify-between gap-3', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('font-semibold text-slate-900', className)}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-slate-500', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center justify-between text-xs text-slate-400', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

const CardActions = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center gap-2 mt-4 pt-3 border-t border-slate-100', className)}
    {...props}
  />
));
CardActions.displayName = 'CardActions';

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  CardActions,
  cardVariants,
};
